import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Search,
  Download,
  Play,
  Square,
  CheckSquare,
  Loader,
  Zap,
  AlertCircle,
} from 'lucide-react';
import { voiceApi } from '../../services/audioApi';

interface ElevenLabsVoice {
  voice_id: string;
  name: string;
  description: string;
  category: string;
  language: string;
  gender: string | null;
  preview_url: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

const ImportElevenLabsModal: React.FC<Props> = ({ isOpen, onClose, onImported }) => {
  const [voices, setVoices] = useState<ElevenLabsVoice[]>([]);
  const [filtered, setFiltered] = useState<ElevenLabsVoice[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set());
      setSearch('');
      setError(null);
      fetchAvailable();
    }
  }, [isOpen]);

  useEffect(() => {
    const term = search.toLowerCase();
    setFiltered(
      voices.filter(
        (v) =>
          v.name.toLowerCase().includes(term) ||
          v.description.toLowerCase().includes(term) ||
          v.language.toLowerCase().includes(term) ||
          (v.gender || '').toLowerCase().includes(term)
      )
    );
  }, [search, voices]);

  const fetchAvailable = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await voiceApi.getElevenLabsAvailable();
      setVoices(res.data.voices);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch ElevenLabs voices');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((v) => v.voice_id)));
    }
  };

  const handlePreview = (voice: ElevenLabsVoice) => {
    if (!voice.preview_url) return;

    if (playingId === voice.voice_id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(voice.preview_url);
    audioRef.current = audio;
    audio.play();
    setPlayingId(voice.voice_id);
    audio.onended = () => setPlayingId(null);
  };

  const handleImport = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    setError(null);
    try {
      await voiceApi.importElevenLabsVoices(Array.from(selected));
      onImported();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Import from ElevenLabs</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, language, gender…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Select all row */}
        {!loading && filtered.length > 0 && (
          <div className="px-6 py-1 flex items-center justify-between">
            <button
              onClick={toggleAll}
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
            >
              {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              {allSelected ? 'Deselect all' : `Select all (${filtered.length})`}
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">{selected.size} selected</span>
          </div>
        )}

        {/* Voice list */}
        <div className="flex-1 overflow-y-auto px-6 py-2 min-h-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500 dark:text-gray-400">
              <Loader className="w-8 h-8 animate-spin" />
              <span className="text-sm">Fetching voices from ElevenLabs…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-red-500">
              <AlertCircle className="w-8 h-8" />
              <span className="text-sm text-center">{error}</span>
              <button onClick={fetchAvailable} className="text-xs text-blue-600 hover:underline">Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-500 dark:text-gray-400">
              <Zap className="w-8 h-8" />
              <span className="text-sm">{voices.length === 0 ? 'All ElevenLabs voices are already imported' : 'No voices match your search'}</span>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((voice) => {
                const isSelected = selected.has(voice.voice_id);
                return (
                  <div
                    key={voice.voice_id}
                    onClick={() => toggleSelect(voice.voice_id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-500'}`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>

                    {/* Voice info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{voice.name}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{voice.category}</span>
                        {voice.language && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">{voice.language.toUpperCase()}</span>
                        )}
                        {voice.gender && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 capitalize">{voice.gender}</span>
                        )}
                      </div>
                      {voice.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{voice.description}</p>
                      )}
                    </div>

                    {/* Preview button */}
                    {voice.preview_url && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePreview(voice); }}
                        className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                        title="Preview voice"
                      >
                        {playingId === voice.voice_id ? <Square className="w-4 h-4 text-blue-600" /> : <Play className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          {error && !loading && (
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={selected.size === 0 || importing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {importing ? (
                <><Loader className="w-4 h-4 animate-spin" /> Importing…</>
              ) : (
                <><Download className="w-4 h-4" /> Import {selected.size > 0 ? `(${selected.size})` : ''}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportElevenLabsModal;
